import React, { useState, useMemo, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Stack, Button, Typography, Checkbox, CircularProgress, Divider,
} from '@mui/material';
import {
  InfoOutlined as InfoOutlinedIcon,
  FileDownloadOutlined as FileDownloadOutlinedIcon,
  SpeedOutlined as SpeedOutlinedIcon,
  TuneOutlined as TuneOutlinedIcon,
  StorageOutlined as StorageOutlinedIcon,
  Check as CheckIcon,
  LayersOutlined as LayersOutlinedIcon,
  CalculateOutlined as CalculateOutlinedIcon,
  DataObjectOutlined as DataObjectOutlinedIcon,
  DescriptionOutlined as DescriptionOutlinedIcon,
  RemoveCircleOutline as RemoveCircleOutlineIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import MUIMultiSelect from '../UI/MUIMultiSelect/MUIMultiSelect';
import { ENTITY_LABELS } from '../../utils/labelMapping';

const FULL_EXPORT_PURPLE = '#6D5BD0';
const FULL_EXPORT_PURPLE_TINT = '#F1EEFC';
const FULL_EXPORT_PURPLE_HOVER = '#5B4BC0';
const RAW_DATA_TEAL = '#0E9C8C';
const RAW_DATA_TEAL_TINT = '#EAF8F0';

const MODULE_COPY = {
  brandAudit: { descriptor: 'audit', showCrossModelSummary: true },
  categoryAnalysis: { descriptor: 'analysis', showCrossModelSummary: true },
  discovery: { descriptor: 'discovery report', showCrossModelSummary: false },
  monitoring: { descriptor: 'monitoring report', showCrossModelSummary: true },
};

function ExportModeCard({ selected, onClick, disabled, icon: Icon, title, description }) {
  return (
    <Box
      onClick={() => !disabled && onClick()}
      sx={{
        flex: 1,
        position: 'relative',
        border: '1.5px solid',
        borderColor: selected ? FULL_EXPORT_PURPLE : 'divider',
        borderRadius: 2,
        p: 2,
        cursor: disabled ? 'not-allowed' : 'pointer',
        bgcolor: selected ? FULL_EXPORT_PURPLE_TINT : 'transparent',
        transition: 'border-color 0.15s, background-color 0.15s',
        '&:hover': !disabled ? { borderColor: FULL_EXPORT_PURPLE } : {},
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 14,
          right: 14,
          width: 18,
          height: 18,
          borderRadius: '50%',
          border: '1.5px solid',
          borderColor: selected ? FULL_EXPORT_PURPLE : 'divider',
          bgcolor: selected ? FULL_EXPORT_PURPLE : 'transparent',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {selected && <CheckIcon sx={{ fontSize: 12, color: '#fff' }} />}
      </Box>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: selected ? FULL_EXPORT_PURPLE : '#fff',
          color: selected ? '#fff' : 'text.secondary',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 1.25,
        }}
      >
        <Icon sx={{ fontSize: 17 }} />
      </Box>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.4 }}>
        {title}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5, display: 'block' }}>
        {description}
      </Typography>
    </Box>
  );
}

function ExportFormatCard({
  selected, onClick, disabled, icon: Icon, title, badge, badgeBg, badgeColor,
  description, points, accentColor, accentTint,
}) {
  return (
    <Box
      onClick={() => !disabled && onClick()}
      sx={{
        flex: 1,
        border: '1.5px solid',
        borderColor: selected ? accentColor : 'divider',
        borderRadius: 2,
        p: 2,
        cursor: disabled ? 'not-allowed' : 'pointer',
        bgcolor: selected ? accentTint : 'transparent',
        transition: 'border-color 0.15s, background-color 0.15s',
        '&:hover': !disabled ? { borderColor: accentColor } : {},
      }}
    >
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 1 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: '#fff',
            color: accentColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon sx={{ fontSize: 17 }} />
        </Box>
        <Box
          sx={{
            width: 18,
            height: 18,
            borderRadius: '50%',
            border: '1.5px solid',
            borderColor: selected ? accentColor : 'divider',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {selected && <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: accentColor }} />}
        </Box>
      </Stack>

      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
        {title}
      </Typography>
      {badge && (
        <Box
          sx={{
            display: 'inline-block',
            px: 1,
            py: 0.25,
            borderRadius: 1,
            bgcolor: badgeBg,
            color: badgeColor,
            fontSize: '0.62rem',
            fontWeight: 700,
            letterSpacing: 0.4,
            mb: 1,
          }}
        >
          {badge}
        </Box>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.5, mb: 1 }}>
        {description}
      </Typography>
      <Stack spacing={0.6} sx={{ pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
        {points.map((pt, i) => (
          <Stack key={i} direction="row" alignItems="flex-start" spacing={0.75}>
            {pt.included !== false
              ? <CheckIcon sx={{ fontSize: 13, color: accentColor, mt: '1px', flexShrink: 0 }} />
              : <Box sx={{ width: 13, textAlign: 'center', color: 'text.disabled', fontSize: 12, flexShrink: 0 }}>—</Box>}
            <Typography
              variant="caption"
              sx={{ color: pt.included !== false ? 'text.secondary' : 'text.disabled', fontSize: '0.72rem', lineHeight: 1.4 }}
            >
              {pt.text}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}

function CompleteFormatCard() {
  return (
    <Box
      sx={{
        border: '1.5px solid',
        borderColor: FULL_EXPORT_PURPLE,
        borderRadius: 2,
        p: 2,
        bgcolor: FULL_EXPORT_PURPLE_TINT,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1,
          bgcolor: FULL_EXPORT_PURPLE,
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <LayersOutlinedIcon sx={{ fontSize: 17 }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.3, flexWrap: 'wrap', gap: 0.5 }}>
          <Typography variant="subtitle2" fontWeight={700}>Complete JSON</Typography>
          <Box
            sx={{
              px: 0.9,
              py: 0.15,
              borderRadius: 1,
              bgcolor: 'rgba(26, 155, 90, 0.12)',
              color: '#1A9B5A',
              fontSize: '0.62rem',
              fontWeight: 700,
              letterSpacing: 0.4,
            }}
          >
            ONE FILE
          </Box>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5, display: 'block' }}>
          Calculated scores and the raw data behind them, in one file. Every key is explained inside the file.
        </Typography>
      </Box>
    </Box>
  );
}

function CompleteFormatSection() {
  return (
    <Box sx={{ mb: 2.5 }}>
      <Typography
        variant="overline"
        sx={{ color: 'text.disabled', letterSpacing: 1, fontSize: '0.65rem', display: 'block', mb: 1.25 }}
      >
        Export Format
      </Typography>
      <CompleteFormatCard />
    </Box>
  );
}

function ExportFormatSelector({ value, onChange, disabled }) {
  return (
    <Box sx={{ mb: 2.5 }}>
      <Typography
        variant="overline"
        sx={{ color: 'text.disabled', letterSpacing: 1, fontSize: '0.65rem', display: 'block', mb: 1.25 }}
      >
        Export Format
      </Typography>
      <Stack direction="row" spacing={1.5}>
        <ExportFormatCard
          selected={value === 'calculated'}
          disabled={disabled}
          onClick={() => onChange('calculated')}
          icon={CalculateOutlinedIcon}
          title="With Calculations"
          badge="RECOMMENDED"
          badgeBg="rgba(26, 155, 90, 0.12)"
          badgeColor="#1A9B5A"
          description="The complete export, ready to use. Includes every calculated score, ranking, and summary exactly as shown in your reports."
          points={[
            { text: 'Calculated scores and rankings' },
            { text: 'Cross-model summaries' },
            { text: 'Leading / lagging comparisons & top 3' },
          ]}
          accentColor={FULL_EXPORT_PURPLE}
          accentTint={FULL_EXPORT_PURPLE_TINT}
        />
        <ExportFormatCard
          selected={value === 'raw'}
          disabled={disabled}
          onClick={() => onChange('raw')}
          icon={DataObjectOutlinedIcon}
          title="Raw Data"
          badge="FOR YOUR OWN ANALYSIS"
          badgeBg="rgba(0, 0, 0, 0.06)"
          badgeColor="text.secondary"
          description="The underlying data behind every metric, with no calculations applied. Choose this if you'd like to build your own dashboards, reports, or analysis with the numbers directly."
          points={[
            { text: 'Original responses for every metric' },
            { text: 'Source references included' },
            { text: 'No calculated scores, rankings, or summaries', included: false },
          ]}
          accentColor={RAW_DATA_TEAL}
          accentTint={RAW_DATA_TEAL_TINT}
        />
      </Stack>
    </Box>
  );
}

function SummaryColumn({ icon: Icon, accent, label, items, marker = 'dot', muted = false }) {
  if (!items || items.length === 0) return null;
  const markerColor = muted ? 'text.disabled' : accent;
  const glyph = marker === 'check' ? '✓' : marker === 'dash' ? '—' : '•';
  return (
    <Box sx={{ bgcolor: '#fff', border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 1.5 }}>
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
        <Box
          sx={{
            width: 22,
            height: 22,
            borderRadius: 0.75,
            bgcolor: muted ? 'rgba(0,0,0,0.06)' : accent,
            color: muted ? 'text.disabled' : '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Icon sx={{ fontSize: 13 }} />
        </Box>
        <Typography
          variant="caption"
          fontWeight={700}
          sx={{ letterSpacing: 0.5, color: 'text.secondary', fontSize: '0.63rem', textTransform: 'uppercase' }}
        >
          {label}
        </Typography>
      </Stack>
      <Stack spacing={0.6}>
        {items.map((item, i) => (
          <Stack key={i} direction="row" alignItems="flex-start" spacing={0.75}>
            <Box component="span" sx={{ color: markerColor, fontSize: 11, lineHeight: '1.5rem', flexShrink: 0 }}>
              {glyph}
            </Box>
            <Typography
              variant="caption"
              sx={{ color: muted ? 'text.disabled' : 'text.secondary', fontSize: '0.72rem', lineHeight: 1.5 }}
            >
              {item}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}

function OptionCard({ checked, onChange, icon: Icon, title, description, note, badge, disabled }) {
  return (
    <Box
      onClick={() => !disabled && onChange(!checked)}
      sx={{
        border: '1.5px solid',
        borderColor: checked ? 'primary.main' : 'divider',
        borderRadius: 2,
        py: 1.5,
        px: 2,
        cursor: disabled ? 'not-allowed' : 'pointer',
        bgcolor: checked ? 'rgba(25, 118, 210, 0.04)' : 'transparent',
        transition: 'border-color 0.15s, background-color 0.15s',
        '&:hover': !disabled
          ? { borderColor: 'primary.main', bgcolor: 'rgba(25, 118, 210, 0.04)' }
          : {},
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.25}>
        <Checkbox
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
          onClick={(e) => e.stopPropagation()}
          sx={{ p: 0 }}
          color="primary"
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
            {Icon && <Icon sx={{ fontSize: 15, color: checked ? 'primary.main' : 'text.secondary' }} />}
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{ color: checked ? 'primary.main' : 'text.primary' }}
            >
              {title}
            </Typography>
            {badge && (
              <Box
                sx={{
                  px: 0.9,
                  py: 0.15,
                  borderRadius: 1,
                  bgcolor: 'warning.light',
                  color: 'warning.dark',
                  fontSize: '0.6rem',
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  lineHeight: 1.5,
                  whiteSpace: 'nowrap',
                }}
              >
                {badge}
              </Box>
            )}
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.55, display: 'block' }}>
            {description}
          </Typography>
          {note && (
            <Typography
              variant="caption"
              sx={{ lineHeight: 1.5, display: 'block', mt: 0.5, color: 'warning.dark', fontStyle: 'italic' }}
            >
              {note}
            </Typography>
          )}
        </Box>
      </Stack>
    </Box>
  );
}

export default function JsonExportConfirmModal({
  open,
  onClose,
  onConfirm,
  loading = false,
  title = 'Customize JSON Export',
  description = 'Select the optional sections to include in the exported JSON. Excluding large datasets can significantly reduce file size and improve generation speed.',
  options = [],
  showSizeEstimator = true,
  type = 'brand',
  showMetrics = false,
  availableMetricKeys = null,
  allowFullExport = false,
  moduleKey = 'brandAudit',
  metricLabels = null,
  // Legacy "With Calculations" vs "Raw Data" choice. Raw data is now merged into the single
  // calculated JSON on the backend, so this defaults to hidden; kept behind a flag for rollback.
  showFormatSelector = false,
}) {
  const moduleCopy = MODULE_COPY[moduleKey] || MODULE_COPY.brandAudit;
  // selections: { [key]: boolean } — all false by default
  const [selections, setSelections] = useState(() =>
    Object.fromEntries(options.map((o) => [o.key, false]))
  );

  const [exportMode, setExportMode] = useState('custom');
  const isFull = allowFullExport && exportMode === 'full';
  const [exportFormat, setExportFormat] = useState('calculated');

  // Re-initialise whenever the modal opens or options change
  useEffect(() => {
    if (open) {
      setSelections(Object.fromEntries(options.map((o) => [o.key, false])));
      setExportMode('custom');
      setExportFormat('calculated');
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const isProduct = type === 'product';

  // Metrics multiselect — which score sections to include in the export; all selected by default.
  // When `availableMetricKeys` is provided (e.g. Category Analysis, where metrics are selectively run),
  // the list is narrowed to only the metrics that actually ran for this transaction.
  const METRIC_OPTIONS = useMemo(() => {
    const all = [
      { key: 'includeVisibility', name: ENTITY_LABELS.rank },
      { key: 'includeSentiment', name: 'Sentiment' },
      { key: 'includePurchaseCriteria', name: ENTITY_LABELS.purchaseCriteria },
      isProduct
        ? { key: 'includeProductDimension', name: ENTITY_LABELS.productDimension }
        : { key: 'includeBrandPillar', name: ENTITY_LABELS.brandPillar },
      { key: 'includePositiveNegativeAttribute', name: isProduct ? ENTITY_LABELS.productAttributes : ENTITY_LABELS.brandAttributes },
    ];
    return availableMetricKeys ? all.filter((m) => availableMetricKeys.includes(m.key)) : all;
  }, [isProduct, availableMetricKeys]);

  const [selectedMetrics, setSelectedMetrics] = useState(() => METRIC_OPTIONS.map((m) => m.key));

  // Re-initialise to "all selected" whenever the modal opens (or the entity type changes)
  useEffect(() => {
    if (open) {
      setSelectedMetrics(METRIC_OPTIONS.map((m) => m.key));
    }
  }, [open, isProduct, availableMetricKeys]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (key, value) =>
    setSelections((prev) => ({ ...prev, [key]: value }));

  // Optional sections tied to a specific metric only make sense while that metric is still
  // selected in the dropdown above — e.g. "Include Sentiment Questions" needs Sentiment checked.
  // Re-evaluated live as the user toggles the Metrics multiselect, not just at open time.
  const visibleOptions = useMemo(
    () => options.filter((o) => o.key !== 'includeSentimentQuestions' || selectedMetrics.includes('includeSentiment')),
    [options, selectedMetrics]
  );

  const checkedCount = useMemo(
    () => visibleOptions.filter((o) => selections[o.key]).length,
    [selections, visibleOptions]
  );

  const sizeInfo = useMemo(() => {
    const total = visibleOptions.length || 1;
    const ratio = checkedCount / total;
    if (checkedCount === 0)
      return { label: 'Compact', hint: 'Fastest generation — minimal payload', color: 'success.main' };
    if (ratio <= 0.5)
      return { label: 'Medium', hint: 'Moderate size — slightly longer generation', color: 'warning.main' };
    return { label: 'Large', hint: 'All sections included — may take longer', color: 'error.main' };
  }, [checkedCount, visibleOptions.length]);

  const isRawFormat = showFormatSelector && exportFormat === 'raw';
  const metricsCoveredItems = showMetrics ? METRIC_OPTIONS.map((m) => m.name) : (metricLabels || []);
  const supportingDetailItems = [
    'Base Response (original model output)',
    ...(options.some((o) => o.key === 'includeSources') ? ['Sources (metric & attribute level)'] : []),
    ...(options.some((o) => o.key === 'includeSentimentQuestions') ? ['Sentiment Questions'] : []),
    ...(options.some((o) => o.key === 'includeConversation') ? ['Conversation Data'] : []),
    ...(!showFormatSelector ? ['Raw API fields', 'Key legend (every key explained)'] : []),
  ];
  const calculatedInsightItems = [
    'Scores & rankings',
    'Leading / lagging positions',
    'Top 3 comparisons',
    ...(moduleCopy.showCrossModelSummary ? ['Cross-Model Summary (2+ LLMs)'] : []),
  ];
  const notIncludedItems = [
    'Calculated scores & rankings',
    'Leading / lagging positions',
    ...(moduleCopy.showCrossModelSummary ? ['Cross-Model Summary'] : []),
  ];

  const handleConfirm = () => {
    if (isFull) {
      const fullSelections = Object.fromEntries(options.map((o) => [o.key, true]));
      // Mutually exclusive by construction — exactly one of these two is ever true.
      // Raw data now ships merged into the calculated JSON, so when the format choice is
      // hidden the export is always the (single) calculated file.
      const formatFlags = showFormatSelector
        ? { includeCalculatedData: exportFormat === 'calculated', includeRawData: exportFormat === 'raw' }
        : { includeCalculatedData: true, includeRawData: false };
      if (!showMetrics) {
        onConfirm({ ...fullSelections, isFullExport: true, ...formatFlags });
        return;
      }
      const allMetricKeys = METRIC_OPTIONS.map((m) => m.key);
      const metrics = {
        includeVisibility: allMetricKeys.includes('includeVisibility'),
        includeSentiment: allMetricKeys.includes('includeSentiment'),
        includeBrandPillar: !isProduct && allMetricKeys.includes('includeBrandPillar'),
        includeProductDimension: isProduct && allMetricKeys.includes('includeProductDimension'),
        includePurchaseCriteria: allMetricKeys.includes('includePurchaseCriteria'),
        includePositiveNegativeAttribute: allMetricKeys.includes('includePositiveNegativeAttribute'),
      };
      onConfirm({ ...fullSelections, isFullExport: true, ...formatFlags, metrics });
      return;
    }

    // Drop selections for sections hidden by the current metric selection (e.g. a stale
    // "Include Sentiment Questions" check from before Sentiment was deselected).
    const sanitizedSelections = Object.fromEntries(
      Object.entries(selections).map(([key, val]) => [key, val && visibleOptions.some((o) => o.key === key)])
    );
    // Only stamp these when this modal instance actually offers the Full Export choice, so
    // other JsonExportConfirmModal usages (e.g. the HTML report modal) keep producing the
    // exact same payload shape as before.
    const fullExportFlag = allowFullExport
      ? { isFullExport: false, includeCalculatedData: true, includeRawData: false }
      : {};

    if (!showMetrics) {
      onConfirm({ ...sanitizedSelections, ...fullExportFlag });
      return;
    }
    if (selectedMetrics.length === 0) {
      toast.warn('Please select at least one metric');
      return;
    }
    const metrics = {
      includeVisibility: selectedMetrics.includes('includeVisibility'),
      includeSentiment: selectedMetrics.includes('includeSentiment'),
      includeBrandPillar: !isProduct && selectedMetrics.includes('includeBrandPillar'),
      includeProductDimension: isProduct && selectedMetrics.includes('includeProductDimension'),
      includePurchaseCriteria: selectedMetrics.includes('includePurchaseCriteria'),
      includePositiveNegativeAttribute: selectedMetrics.includes('includePositiveNegativeAttribute'),
    };
    onConfirm({ ...sanitizedSelections, ...fullExportFlag, metrics });
  };

  const handleClose = () => {
    if (!loading) onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, width: '100%', maxWidth: 700 } }}
    >
      {/* ── Header ── */}
      <DialogTitle sx={{ pb: 0.5 }}>
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <FileDownloadOutlinedIcon sx={{ color: 'primary.main', fontSize: 22 }} />
          <Typography variant="h6" fontWeight={700}>{title}</Typography>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: 1.5 }}>
        {/* Custom vs Full export mode picker */}
        {allowFullExport && (
          <Stack direction="row" spacing={1.5} sx={{ mb: 2.5 }}>
            <ExportModeCard
              selected={!isFull}
              disabled={loading}
              onClick={() => setExportMode('custom')}
              icon={TuneOutlinedIcon}
              title="Custom Export"
              description="Choose specific metrics and optional sections to include."
            />
            <ExportModeCard
              selected={isFull}
              disabled={loading}
              onClick={() => setExportMode('full')}
              icon={StorageOutlinedIcon}
              title="Full Export"
              description="Everything: all metrics, sections, base responses & explanations."
            />
          </Stack>
        )}

        {isFull && (
          showFormatSelector
            ? <ExportFormatSelector value={exportFormat} onChange={setExportFormat} disabled={loading} />
            : <CompleteFormatSection />
        )}

        {isFull ? (
          /* Full Export summary — no picking, everything is included. Content and wording
             both react to the chosen Export Format (calculated vs raw). */
          <Box
            sx={{
              border: '1px solid',
              borderColor: isRawFormat ? '#CFEEDC' : '#E3DEFA',
              background: isRawFormat
                ? 'linear-gradient(180deg, #FAFEFC, #F3FBF7)'
                : 'linear-gradient(180deg, #FBFAFF, #F6F3FE)',
              borderRadius: 2,
              p: 2.25,
              mb: 2.5,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.1} sx={{ mb: 1 }}>
              <LayersOutlinedIcon sx={{ color: isRawFormat ? RAW_DATA_TEAL : FULL_EXPORT_PURPLE, fontSize: 20 }} />
              <Typography variant="subtitle2" fontWeight={700} sx={{ color: isRawFormat ? '#12603F' : '#3F3184' }}>
                {isRawFormat
                  ? 'The original data — no calculations applied'
                  : `The complete ${moduleCopy.descriptor} — nothing left out`}
              </Typography>
            </Stack>
            <Typography variant="body2" sx={{ color: isRawFormat ? '#3F6D5A' : '#5B5480', lineHeight: 1.6, mb: 1.5 }}>
              {isRawFormat
                ? "A single file containing the original data behind every metric, exactly as received — without any calculated scores, rankings, or summaries applied. Recommended for building your own dashboards, reports, or analysis."
                : `A single file containing the complete ${moduleCopy.descriptor}: the calculated scores and the raw data behind them, with every key explained. Recommended for a full record to share with your team, hand off to an agency, or retain for future reference.`}
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 1.25, mb: 1.5 }}>
              <SummaryColumn
                icon={LayersOutlinedIcon}
                accent={isRawFormat ? RAW_DATA_TEAL : FULL_EXPORT_PURPLE}
                label="Metrics Covered"
                items={metricsCoveredItems}
                marker="dot"
              />
              {isRawFormat ? (
                <>
                  <SummaryColumn
                    icon={DescriptionOutlinedIcon}
                    accent={RAW_DATA_TEAL}
                    label="Supporting Detail"
                    items={supportingDetailItems}
                    marker="check"
                  />
                  <SummaryColumn
                    icon={RemoveCircleOutlineIcon}
                    accent={RAW_DATA_TEAL}
                    label="Not Included"
                    items={notIncludedItems}
                    marker="dash"
                    muted
                  />
                </>
              ) : (
                <>
                  <SummaryColumn
                    icon={CalculateOutlinedIcon}
                    accent={FULL_EXPORT_PURPLE}
                    label="Calculated Insights"
                    items={calculatedInsightItems}
                    marker="check"
                  />
                  <SummaryColumn
                    icon={DescriptionOutlinedIcon}
                    accent={FULL_EXPORT_PURPLE}
                    label="Supporting Detail"
                    items={supportingDetailItems}
                    marker="check"
                  />
                </>
              )}
            </Box>

            <Divider sx={{ borderColor: isRawFormat ? '#CFEEDC' : '#E3DEFA', mb: 1 }} />
            <Typography variant="caption" sx={{ color: isRawFormat ? '#3F6D5A' : '#5B5480', lineHeight: 1.5, display: 'block' }}>
              {isRawFormat
                ? 'This format contains source data only — any scoring, ranking, or analysis is left for you to apply.'
                : "Together, this provides complete traceability — from each model's original response through to every calculated score."}
            </Typography>
          </Box>
        ) : (
          <>
            {/* Info banner */}
            <Box
              sx={{
                display: 'flex',
                gap: 1.25,
                bgcolor: 'rgba(2, 136, 209, 0.06)',
                border: '1px solid rgba(2, 136, 209, 0.25)',
                borderRadius: 1.5,
                p: 1.5,
                mb: 2.5,
              }}
            >
              <InfoOutlinedIcon sx={{ color: 'info.main', fontSize: 18, mt: 0.15, flexShrink: 0 }} />
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
                {description}
              </Typography>
            </Box>

            {/* Metrics multiselect — which score sections to include */}
            {showMetrics && (
              <Box sx={{ mb: 2.5 }}>
                <MUIMultiSelect
                  label="Metrics"
                  disabled={loading}
                  options={METRIC_OPTIONS.map((m) => ({ name: m.name, value: m.key }))}
                  value={selectedMetrics}
                  renderSelectedValues={(selected) =>
                    METRIC_OPTIONS.filter((m) => selected.includes(m.key)).map((m) => m.name).join(', ')
                  }
                  onChange={(selected) => setSelectedMetrics(selected)}
                />
              </Box>
            )}

            {/* Section label */}
            {visibleOptions.length > 0 && (
              <Typography
                variant="overline"
                sx={{ color: 'text.disabled', letterSpacing: 1, fontSize: '0.65rem', display: 'block', mb: 1.25 }}
              >
                Optional Sections
              </Typography>
            )}

            <Stack spacing={1.25}>
              {visibleOptions.map((opt) => (
                <OptionCard
                  key={opt.key}
                  checked={!!selections[opt.key]}
                  onChange={(val) => toggle(opt.key, val)}
                  icon={opt.icon}
                  title={opt.title}
                  description={opt.description}
                  note={opt.note}
                  badge={opt.badge}
                  disabled={loading}
                />
              ))}
            </Stack>

            {/* Export size estimator — only shown when there are options */}
            {visibleOptions.length > 0 && showSizeEstimator && (
              <Box
                sx={{
                  mt: 2.5,
                  p: 1.5,
                  borderRadius: 1.5,
                  bgcolor: 'rgba(0,0,0,0.03)',
                  border: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <SpeedOutlinedIcon sx={{ fontSize: 16, color: sizeInfo.color, flexShrink: 0 }} />
                <Typography variant="caption" color="text.secondary">
                  Estimated export size:{' '}
                  <Box component="span" sx={{ color: sizeInfo.color, fontWeight: 700 }}>
                    {sizeInfo.label}
                  </Box>
                  {' — '}{sizeInfo.hint}
                </Typography>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 2.5, py: 1.75, gap: 1 }}>
        <Button onClick={handleClose} disabled={loading} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={loading}
          startIcon={
            loading
              ? <CircularProgress size={15} color="inherit" thickness={4.5} />
              : <FileDownloadOutlinedIcon />
          }
          sx={{
            minWidth: 170,
            ...(isFull && {
              bgcolor: isRawFormat ? RAW_DATA_TEAL : FULL_EXPORT_PURPLE,
              '&:hover': { bgcolor: isRawFormat ? '#0C8577' : FULL_EXPORT_PURPLE_HOVER },
            }),
          }}
        >
          {loading ? 'Generating…' : (isFull ? 'Generate Full Export' : 'Generate & Download')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
